import React, { useEffect, useState } from 'react';
import ModalComponent from '@components/ModalComponent';
import Button from '@components/legacy/core/Button';
import Icon from '@components/core/Icon';
import Chip from '@components/core/Chip';
import Select2 from '@components/legacy/core/Select2';
import { useTranslations } from '@components/context';
import {
    AUTOMATION_MENU_OPTION_IDS,
    AUTOMATION_TRIGGER_OPTIONS,
    AUTOMATION_CONDITION_OPTIONS,
    AUTOMATION_SEND_TIMING_OPTIONS,
    AUTOMATION_LANGUAGE_OPTIONS,
    AUTOMATION_MESSAGE_TEMPLATE_OPTIONS,
    MOVE_STEP_OPTIONS,
    automationModalTitle,
    isMessageAutomationKind,
    isScheduledTriggerId,
} from '../../config';
import ScheduledAutomationSidebar from '../ScheduledAutomationSidebar';
import RecruitmentFlowBar from '../RecruitmentFlowBar';
import './AddAutomationModal.less';

const SUMMARY_MAX = 42;

function truncateSummary(text) {
    const s = (text || '').replace(/\s+/g, ' ').trim();
    if (s.length <= SUMMARY_MAX) return s;
    return `${s.slice(0, SUMMARY_MAX - 1)}…`;
}

const ids = {
    subject: 'auto-modal-subject',
    message: 'auto-modal-message',
    detail: 'auto-modal-detail',
};

const SELECT_IDS = {
    trigger: 'automation-modal-select-trigger',
    conditions: 'automation-modal-select-conditions',
    language: 'automation-modal-select-language',
    template: 'automation-modal-select-template',
    sendTiming: 'automation-modal-select-send-timing',
};

const DEFAULT_TAGS = ['Good candidate', 'Self-joined', 'Marketing'];

const DEFAULT_SCHEDULED_UI = {
    frequency: 'daily',
    time: '09:00',
    combine: 'and',
    selectedQuestionId: '',
    answers: {},
};

const TRIGGER_OPTIONS = AUTOMATION_TRIGGER_OPTIONS.map((o) => ({ id: o.id, text: o.label }));
const CONDITION_OPTIONS = AUTOMATION_CONDITION_OPTIONS.map((o) => ({ id: o.id, text: o.label }));
const LANGUAGE_OPTIONS = AUTOMATION_LANGUAGE_OPTIONS.map((o) => ({ id: o.code, text: o.name }));
const TEMPLATE_OPTIONS = AUTOMATION_MESSAGE_TEMPLATE_OPTIONS.map((o) => ({ id: o.id, text: o.label }));
const SEND_TIMING_OPTIONS = AUTOMATION_SEND_TIMING_OPTIONS.map((o) => ({ id: o.id, text: o.label }));

const AddAutomationModal = ({ show, menuOption, onClose, onConfirm }) => {
    const { t } = useTranslations();
    const [triggerId, setTriggerId] = useState('');
    const [conditionIds, setConditionIds] = useState([]);
    const [templateId, setTemplateId] = useState('');
    const [subject, setSubject] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [languageCode, setLanguageCode] = useState('en');
    const [sendTimingId, setSendTimingId] = useState('immediate');
    const [detailText, setDetailText] = useState('');
    const [scheduledUi, setScheduledUi] = useState(() => ({ ...DEFAULT_SCHEDULED_UI }));
    const [tags, setTags] = useState([...DEFAULT_TAGS]);
    const [selectedStepId, setSelectedStepId] = useState('');
    const [newTagInput, setNewTagInput] = useState('');
    const [showTagInput, setShowTagInput] = useState(false);

    const isMessage = menuOption && isMessageAutomationKind(menuOption.id);
    const isTag = menuOption?.id === AUTOMATION_MENU_OPTION_IDS.ADD_TAG;
    const isMoveStep = menuOption?.id === AUTOMATION_MENU_OPTION_IDS.MOVE_STEP;

    useEffect(() => {
        if (!show) return;
        setTriggerId('');
        setConditionIds([]);
        setTemplateId('');
        setSubject('');
        setMessageBody('');
        setLanguageCode('en');
        setSendTimingId('immediate');
        setDetailText('');
        setScheduledUi({ ...DEFAULT_SCHEDULED_UI });
        setTags([...DEFAULT_TAGS]);
        setSelectedStepId('');
        setNewTagInput('');
        setShowTagInput(false);
    }, [show, menuOption?.id]);

    useEffect(() => {
        if (!isScheduledTriggerId(triggerId)) {
            setScheduledUi({ ...DEFAULT_SCHEDULED_UI });
        }
    }, [triggerId]);

    if (!menuOption) return null;

    const handleAddTag = () => {
        const trimmed = newTagInput.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags((prev) => [...prev, trimmed]);
        }
        setNewTagInput('');
        setShowTagInput(false);
    };

    const handleRemoveTag = (tag) => setTags((prev) => prev.filter((t) => t !== tag));

    const handleTagInputKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); }
        if (e.key === 'Escape') { setShowTagInput(false); setNewTagInput(''); }
    };

    const handleConfirm = () => {
        const chipIconClass = menuOption.iconClass;
        const prefix = isMessage ? t('Message') : menuOption.label;
        const detail = detailText.trim();
        const subj = subject.trim();
        const body = messageBody.trim();

        let summaryText;
        let fullText;
        if (isMessage) {
            if (subj) {
                summaryText = truncateSummary(`${prefix}: ${subj}`);
                fullText = `${prefix}: ${subj}\n\n${body}`;
            } else if (body) {
                summaryText = truncateSummary(`${prefix}: ${body}`);
                fullText = `${prefix}:\n\n${body}`;
            } else {
                summaryText = truncateSummary(`${prefix}: …`);
                fullText = `${prefix}: …`;
            }
        } else if (detail) {
            summaryText = truncateSummary(`${prefix}: ${detail}`);
            fullText = `${prefix}: ${detail}`;
        } else {
            summaryText = truncateSummary(`${prefix}: …`);
            fullText = `${prefix}: …`;
        }

        onConfirm({
            kind: menuOption.id,
            chipIconClass,
            summaryText,
            fullText,
            meta: {
                triggerId: triggerId || null,
                conditionIds: isScheduledTriggerId(triggerId) ? [] : conditionIds,
                subject: isMessage ? subj : '',
                messageBody: isMessage ? body : '',
                languageCode: isMessage ? languageCode : null,
                sendTimingId: isMessage ? sendTimingId : null,
                detailText: !isMessage ? detail : '',
                scheduledTrigger: isScheduledTriggerId(triggerId) ? scheduledUi : null,
            },
        });
        onClose();
    };

    const detailLabel =
        menuOption.id === AUTOMATION_MENU_OPTION_IDS.ADD_NOTE ? t('Note') : t('Details');

    const select2Parent = '.add-automation-modal';

    return (
        <ModalComponent show={show} closeModal={onClose}>
            <div className="overlay add-automation-modal">
                <div className="overlay-header add-automation-modal__header">
                    <h3>{automationModalTitle(menuOption, t)}</h3>
                    <Button
                        type="link"
                        className="overlay-cancel overlay-cancel-mark"
                        aria-label={t('Close')}
                        onClick={onClose}
                    />
                </div>
                <div className="overlay-main add-automation-modal__main">
                    <div className="add-automation-modal__layout">
                        <aside className="add-automation-modal__sidebar">
                            <div className="add-automation-modal__sidebar-section">
                                <div className="add-automation-modal__sidebar-heading">
                                    {t('Trigger when…')}
                                </div>
                                <Select2
                                    id={SELECT_IDS.trigger}
                                    className="add-automation-modal__select2 add-automation-modal__select2--block"
                                    value={triggerId}
                                    onChange={(v) => setTriggerId(v || '')}
                                    options={TRIGGER_OPTIONS}
                                    placeholder={t('Trigger type')}
                                    allowClear={false}
                                    allowEmpty
                                    hideSearch
                                    parentSelector={select2Parent}
                                />
                            </div>
                            {isScheduledTriggerId(triggerId) ? (
                                <div className="add-automation-modal__sidebar-section add-automation-modal__sidebar-section--scheduled">
                                    <ScheduledAutomationSidebar
                                        value={scheduledUi}
                                        onChange={setScheduledUi}
                                        t={t}
                                        select2ParentSelector={select2Parent}
                                    />
                                </div>
                            ) : (
                                <div className="add-automation-modal__sidebar-section">
                                    <div className="add-automation-modal__sidebar-heading">
                                        {t('Given these conditions...')}
                                    </div>
                                    <Select2
                                        id={SELECT_IDS.conditions}
                                        className="add-automation-modal__select2 add-automation-modal__select2--block add-automation-modal__select2--conditions"
                                        multiple
                                        value={conditionIds}
                                        onChange={(v) => setConditionIds(v || [])}
                                        options={CONDITION_OPTIONS}
                                        placeholder={t('Add optional condition')}
                                        allowClear
                                        allowEmpty
                                        hideSearch
                                        parentSelector={select2Parent}
                                    />
                                </div>
                            )}
                        </aside>
                        <div className="add-automation-modal__body">
                            {isMessage ? (
                                <>
                                    <div className="add-automation-modal__field-row add-automation-modal__field-row--from">
                                        <span className="add-automation-modal__field-label">{t('From:')}</span>
                                        <Button
                                            type="link"
                                            className="add-automation-modal__from-trigger"
                                        >
                                            <span className="add-automation-modal__from-name">
                                                {t('Example sender')}
                                            </span>
                                            <span className="add-automation-modal__from-email">
                                                &lt;noreply@example.com&gt;
                                            </span>
                                            <Icon
                                                value="chevron-down"
                                                className="add-automation-modal__field-chevron"
                                                ariaHidden
                                            />
                                        </Button>
                                    </div>
                                    <div className="add-automation-modal__field-row add-automation-modal__field-row--subject">
                                        <label
                                            className="add-automation-modal__field-label"
                                            htmlFor={ids.subject}
                                        >
                                            {t('Subject:')}
                                        </label>
                                        <div className="add-automation-modal__subj-wrap">
                                            <input
                                                id={ids.subject}
                                                type="text"
                                                className="add-automation-modal__subj-input"
                                                placeholder={t('Message subject')}
                                                value={subject}
                                                onChange={(e) => setSubject(e.target.value)}
                                            />
                                        </div>
                                        <div className="add-automation-modal__lang-dd-wrap">
                                            <Select2
                                                id={SELECT_IDS.language}
                                                className="add-automation-modal__select2 add-automation-modal__select2--lang"
                                                value={languageCode}
                                                onChange={(v) => setLanguageCode(v || 'en')}
                                                options={LANGUAGE_OPTIONS}
                                                allowClear={false}
                                                allowEmpty={false}
                                                hideSearch
                                                parentSelector={select2Parent}
                                            />
                                        </div>
                                    </div>
                                    <div className="add-automation-modal__message-block">
                                        <div className="add-automation-modal__message-head">
                                            <div className="add-automation-modal__message-head-title">
                                                <label
                                                    className="add-automation-modal__label add-automation-modal__label--inline"
                                                    htmlFor={ids.message}
                                                >
                                                    {t('Message')}
                                                </label>
                                                <Icon
                                                    value="question-circle"
                                                    className="add-automation-modal__message-info"
                                                    ariaHidden
                                                />
                                            </div>
                                            <div className="add-automation-modal__message-toolbar">
                                                <Select2
                                                    id={SELECT_IDS.template}
                                                    className="add-automation-modal__select2 add-automation-modal__select2--toolbar"
                                                    value={templateId}
                                                    onChange={(v) => setTemplateId(v || '')}
                                                    options={TEMPLATE_OPTIONS}
                                                    placeholder={t('Use a template')}
                                                    allowClear={false}
                                                    allowEmpty
                                                    hideSearch
                                                    parentSelector={select2Parent}
                                                />
                                                <Button
                                                    type="ai"
                                                    title={t('Generate')}
                                                    onClick={() => {}}
                                                    className="add-automation-modal__ai-gen"
                                                />
                                            </div>
                                        </div>
                                        <textarea
                                            id={ids.message}
                                            className="form-control add-automation-modal__textarea"
                                            rows={10}
                                            placeholder={t('Write your message…')}
                                            value={messageBody}
                                            onChange={(e) => setMessageBody(e.target.value)}
                                        />
                                    </div>
                                    <div className="add-automation-modal__send-row">
                                        <span
                                            className="add-automation-modal__send-attach-icon"
                                            aria-hidden
                                        >
                                            <Icon value="paperclip" />
                                        </span>
                                        <div className="add-automation-modal__send-select-wrap">
                                            <Select2
                                                id={SELECT_IDS.sendTiming}
                                                className="add-automation-modal__select2 add-automation-modal__select2--block"
                                                value={sendTimingId}
                                                onChange={(v) =>
                                                    setSendTimingId(
                                                        v || AUTOMATION_SEND_TIMING_OPTIONS[0].id,
                                                    )
                                                }
                                                options={SEND_TIMING_OPTIONS}
                                                allowClear={false}
                                                allowEmpty={false}
                                                hideSearch
                                                parentSelector={select2Parent}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : isTag ? (
                                <div className="add-automation-modal__tag-body">
                                    <h5 className="add-automation-modal__tag-heading">{t('Add a tag')}</h5>
                                    <div className="about-chip-wrap add-automation-modal__tag-row">
                                        {tags.map((tag) => (
                                            <Chip
                                                key={tag}
                                                value={tag}
                                                size="sm"
                                                noBorder
                                                variant="secondary"
                                                onRemove={() => handleRemoveTag(tag)}
                                            />
                                        ))}
                                        <div className="add-automation-modal__tag-add-row">
                                            {showTagInput ? (
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        className="form-control add-automation-modal__tag-input"
                                                        placeholder={t('Tag name')}
                                                        value={newTagInput}
                                                        onChange={(e) => setNewTagInput(e.target.value)}
                                                        onKeyDown={handleTagInputKeyDown}
                                                    />
                                                    <span
                                                        className="input-group-addon"
                                                        onClick={handleAddTag}
                                                    >
                                                        +
                                                    </span>
                                                </div>
                                            ) : (
                                                <Button
                                                    type="link"
                                                    className="add-automation-modal__tag-add-btn"
                                                    icon="icon-svg-plus"
                                                    onClick={() => setShowTagInput(true)}
                                                >
                                                    {t('Add tag')}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : isMoveStep ? (
                                <div className="add-automation-modal__move-step-placeholder">
                                    <h5 className="add-automation-modal__tag-heading">{t('Move candidates to')}</h5>
                                    <RecruitmentFlowBar
                                        steps={MOVE_STEP_OPTIONS}
                                        value={selectedStepId}
                                        onChange={setSelectedStepId}
                                    />
                                </div>
                            ) : (
                                <>
                                    <label
                                        className="add-automation-modal__label"
                                        htmlFor={ids.detail}
                                    >
                                        {detailLabel}
                                    </label>
                                    <textarea
                                        id={ids.detail}
                                        className="form-control add-automation-modal__textarea"
                                        rows={12}
                                        placeholder={t('Describe this automation…')}
                                        value={detailText}
                                        onChange={(e) => setDetailText(e.target.value)}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="overlay-footer add-automation-modal__footer">
                    <div className="btn-container">
                        <Button
                            type="default"
                            v2
                            className="add-automation-modal__footer-cancel"
                            onClick={onClose}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button type="primary" className="add-automation-modal__submit" onClick={handleConfirm}>
                            {t('Add automation')}
                        </Button>
                    </div>
                </div>
            </div>
        </ModalComponent>
    );
};

export default AddAutomationModal;
